// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    makeStyles,
    mergeClasses,
    Spinner,
    Textarea,
    tokens,
    Toast,
    ToastBody,
    ToastTitle,
    useToastController,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import React, { useCallback, useEffect, useState } from 'react';
import { FeedbackResponse, FeedbackService } from '../../libs/services/FeedbackService';
import { AuthHelper } from '../../libs/auth/AuthHelper';
import { StarRating } from './StarRating';

const useClasses = makeStyles({
    dialogSurface: {
        maxWidth: '480px',
        width: '100%',
        '@media (max-width: 600px)': {
            maxWidth: '100%',
            margin: '16px',
        },
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    closeButton: {
        minWidth: '32px',
        minHeight: '32px',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        paddingTop: '8px',
    },
    ratingSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    ratingLabel: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    commentSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    characterCount: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        textAlign: 'right',
    },
    characterCountWarning: {
        color: tokens.colorPaletteRedForeground1,
    },
    actions: {
        justifyContent: 'flex-end',
        gap: '8px',
        paddingTop: '8px',
    },
    submitButton: {
        minWidth: '100px',
    },
    spinner: {
        marginRight: '8px',
    },
});

interface FeedbackModalProps {
    /** Whether the modal is open */
    isOpen: boolean;
    /** Callback when modal closes */
    onClose: () => void;
    /** The agent ID for the feedback */
    agentId: string;
    /** The session ID for the feedback */
    sessionId: string;
    /** The message ID for the feedback */
    messageId: string;
    /** Optional existing feedback to pre-fill (for edit mode) */
    existingFeedback?: FeedbackResponse | null;
}

const MAX_COMMENT_LENGTH = 500;
const feedbackService = new FeedbackService();

/**
 * FeedbackModal component for submitting or editing feedback on agent responses.
 * Supports both create and update operations based on existing feedback.
 */
export const FeedbackModal: React.FC<FeedbackModalProps> = ({
    isOpen,
    onClose,
    agentId,
    sessionId,
    messageId,
    existingFeedback,
}) => {
    const classes = useClasses();
    const { dispatchToast } = useToastController();
    const { instance, inProgress } = useMsal();

    const [rating, setRating] = useState<number>(existingFeedback?.rating ?? 0);
    const [comment, setComment] = useState<string>(existingFeedback?.comment ?? '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_hasSubmitted, setHasSubmitted] = useState(false);

    const isEditMode = existingFeedback !== null && existingFeedback !== undefined;
    const characterCount = comment.length;
    const isOverLimit = characterCount > MAX_COMMENT_LENGTH;

    // Reset form when modal opens or existingFeedback changes
    useEffect(() => {
        if (isOpen) {
            setRating(existingFeedback?.rating ?? 0);
            setComment(existingFeedback?.comment ?? '');
            setHasSubmitted(false);
        }
    }, [isOpen, existingFeedback]);

    const showToast = useCallback(
        (title: string, body: string, intent: 'success' | 'error') => {
            dispatchToast(
                <Toast>
                    <ToastTitle>{title}</ToastTitle>
                    <ToastBody>{body}</ToastBody>
                </Toast>,
                { intent, timeout: 3000 },
            );
        },
        [dispatchToast],
    );

    const handleSubmit = useCallback(async () => {
        if (rating === 0) {
            showToast('Hata', 'Lütfen bir puan seçin.', 'error');
            return;
        }

        if (isOverLimit) {
            showToast('Hata', 'Yorum çok uzun.', 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);

            if (existingFeedback) {
                // Update existing feedback
                await feedbackService.updateFeedback(
                    existingFeedback.id,
                    {
                        rating,
                        comment: comment.trim() || undefined,
                    },
                    accessToken,
                );
                showToast('Başarılı!', 'Geri bildiriminiz güncellendi.', 'success');
            } else {
                // Create new feedback
                await feedbackService.createFeedback(
                    {
                        agentId,
                        sessionId,
                        messageId,
                        rating,
                        comment: comment.trim() || undefined,
                    },
                    accessToken,
                );
                showToast('Teşekkürler!', 'Geri bildiriminiz kaydedildi.', 'success');
            }

            setHasSubmitted(true);
            onClose();
        } catch (error: unknown) {
            console.error('Feedback submission error:', error);

            // Handle specific errors
            const errorMessage = error instanceof Error ? error.message : String(error);
            if (errorMessage.includes('409')) {
                showToast('Hata', 'Bu mesaj için zaten geri bildirim verilmiş.', 'error');
            } else if (errorMessage.includes('400')) {
                showToast('Hata', 'Geçersiz istek. Lütfen tekrar deneyin.', 'error');
            } else {
                showToast('Hata', 'Geri bildirim gönderilemedi. Lütfen tekrar deneyin.', 'error');
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [
        rating,
        comment,
        isOverLimit,
        existingFeedback,
        agentId,
        sessionId,
        messageId,
        instance,
        inProgress,
        showToast,
        onClose,
    ]);

    const handleCommentChange = useCallback((ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComment(ev.target.value);
    }, []);

    const handleKeyDown = useCallback(
        (ev: React.KeyboardEvent) => {
            if (ev.key === 'Escape') {
                onClose();
            }
        },
        [onClose],
    );

    return (
        <Dialog open={isOpen} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
            <DialogSurface className={classes.dialogSurface} onKeyDown={handleKeyDown}>
                <DialogBody>
                    <DialogTitle className={classes.dialogTitle}>
                        <span>{isEditMode ? 'Geri Bildirimi Düzenle' : 'Yanıtı Değerlendir'}</span>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance="subtle"
                                className={classes.closeButton}
                                icon={<Dismiss24Regular />}
                                aria-label="Kapat"
                                onClick={onClose}
                            />
                        </DialogTrigger>
                    </DialogTitle>

                    <DialogContent className={classes.content}>
                        <div className={classes.ratingSection}>
                            <label className={classes.ratingLabel} id="rating-label">
                                Bu yanıtı nasıl buldunuz?
                            </label>
                            <StarRating
                                value={rating}
                                onChange={setRating}
                                disabled={isSubmitting}
                                showLabel={true}
                            />
                        </div>

                        <Field
                            label="Yorumunuz (isteğe bağlı)"
                            className={classes.commentSection}
                            hint={
                                <span
                                    className={mergeClasses(
                                        classes.characterCount,
                                        isOverLimit ? classes.characterCountWarning : undefined,
                                    )}
                                >
                                    {characterCount}/{MAX_COMMENT_LENGTH}
                                </span>
                            }
                        >
                            <Textarea
                                value={comment}
                                onChange={handleCommentChange}
                                placeholder="Yanıt hakkında düşüncelerinizi paylaşın..."
                                resize="vertical"
                                rows={3}
                                disabled={isSubmitting}
                                aria-describedby="comment-hint"
                            />
                        </Field>
                    </DialogContent>

                    <DialogActions className={classes.actions}>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary" disabled={isSubmitting} onClick={onClose}>
                                İptal
                            </Button>
                        </DialogTrigger>
                        <Button
                            appearance="primary"
                            className={classes.submitButton}
                            onClick={() => { void handleSubmit(); }}
                            disabled={isSubmitting || rating === 0 || isOverLimit}
                        >
                            {isSubmitting && <Spinner size="tiny" className={classes.spinner} />}
                            {isEditMode ? 'Güncelle' : 'Gönder'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
